import { Avatar, Chip, Spinner } from '@heroui/react';
import React from 'react';
import { Link } from 'react-router-dom';

import { ErrorCard } from '../../../../components/ErrorCard';
import { SidePanel } from '../../../../components/SidePanel';
import { useWorkspaceMember } from '../../hooks/useWorkspaceMember';
import type { DirectoryMember, MemberAssignment } from '../../types';
import {
  locationLabel,
  memberInitials,
  memberName,
  tierStyle,
} from '../../utils';

export interface MemberDetailPanelProps {
  workspaceId: string;
  /** The member being read. Null closes the panel and skips the read. */
  userId: string | null;
  onClose: () => void;
}

const Field: React.FC<{ label: string; value: string | null }> = ({
  label,
  value,
}) => (
  <div>
    <p className="text-tiny uppercase tracking-wide text-default-400">
      {label}
    </p>
    <p className="break-words text-small text-foreground">{value ?? '-'}</p>
  </div>
);

/** One grant, read as the place under the tier above it, then the role held. */
const AssignmentRow: React.FC<{ assignment: MemberAssignment }> = ({
  assignment,
}) => {
  const tier = tierStyle(assignment.hierarchyType);

  return (
    <div className="flex items-start justify-between gap-3 border-b border-default-100 py-3 last:border-b-0">
      <div className="flex items-start gap-2">
        <span
          className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${tier.dot}`}
          aria-hidden
        />
        <div>
          <p className="text-small font-medium text-foreground">
            {locationLabel(assignment.locationName, assignment.locationId)}
          </p>
          {assignment.parentName && (
            <p className="text-tiny text-default-400">
              {assignment.parentName}
            </p>
          )}
        </div>
      </div>
      <div className="text-right">
        <Chip size="sm" variant="flat">
          {assignment.role.displayName}
        </Chip>
        {assignment.role.description && (
          <p className="mt-1 max-w-56 text-tiny text-default-400">
            {assignment.role.description}
          </p>
        )}
      </div>
    </div>
  );
};

const MemberBody: React.FC<{ member: DirectoryMember }> = ({ member }) => (
  <div className="flex flex-col gap-6 p-6">
    <div className="flex items-center gap-3">
      <Avatar
        size="lg"
        src={member.avatarUrl ?? undefined}
        name={memberInitials(member.name)}
        showFallback
      />
      <div>
        <p className="text-large font-semibold text-foreground">
          {memberName(member.name)}
        </p>
        <Link
          to={`/users/${member.userId}`}
          className="text-tiny text-default-500 hover:text-primary hover:underline"
        >
          Open the full user record
        </Link>
      </div>
    </div>

    <div className="flex flex-col gap-4">
      <Field label="Email address" value={member.email} />
      <Field label="Contact number" value={member.phone} />
    </div>

    <div>
      <p className="mb-1 text-tiny uppercase tracking-wide text-default-400">
        Assignments
      </p>
      {member.assignments.length === 0 ? (
        <p className="py-3 text-small text-default-500">
          They are a member of the workspace and hold no role in it.
        </p>
      ) : (
        member.assignments.map((assignment) => (
          <AssignmentRow
            key={`${assignment.locationId}-${assignment.role.id}`}
            assignment={assignment}
          />
        ))
      )}
    </div>
  </div>
);

/**
 * One member of the workspace, opened from the directory.
 *
 * The directory lists everyone in the workspace, so the reader can pick someone
 * they share no location with. The point read answers for them too, which is
 * what the paged reads cannot do.
 */
export const MemberDetailPanel: React.FC<MemberDetailPanelProps> = ({
  workspaceId,
  userId,
  onClose,
}) => {
  const { member, isLoading, error, notFound, notAMember } = useWorkspaceMember(
    workspaceId,
    userId
  );

  return (
    <SidePanel
      isOpen={userId !== null}
      onClose={onClose}
      title="Team member"
      size="md"
    >
      {error && (
        <div className="p-6">
          <ErrorCard
            title="Could not load the member"
            message={error.message}
          />
        </div>
      )}

      {!error && notFound && (
        <div className="p-6">
          <ErrorCard
            title="Workspace not found"
            message="Check the link, or pick the workspace from the list."
          />
        </div>
      )}

      {!error && notAMember && (
        <div className="p-6">
          <ErrorCard
            title="No longer a member"
            message="They have left this workspace since the directory was read."
          />
        </div>
      )}

      {/* The read is skipped while closed, so a spinner only shows for a real
          question in flight. */}
      {!error && !notFound && !notAMember && !member && isLoading && (
        <div className="flex justify-center p-10">
          <Spinner size="sm" />
        </div>
      )}

      {!error && !notFound && !notAMember && member && (
        <MemberBody member={member} />
      )}
    </SidePanel>
  );
};
